"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { Header, Footer } from "@repo/ui";
import { ReactNode, Suspense } from "react";
import { useInactivityTimeout } from "../hooks/useInactivityTimeout";
import InactivityWarningModal from "./InactivityWarningModal";
import { useUser } from "../contexts/UserContext";

interface ConditionalLayoutProps {
  children: ReactNode;
}

function ConditionalLayoutContent({ children }: ConditionalLayoutProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { showWarning, remainingTime, extendSession, logout } = useInactivityTimeout();
  const { profile } = useUser();

  // Don't show header/footer on dashboard, intake form, or the MFA gate.
  // /mfa in particular must NOT expose the header's "Dashboard" link —
  // it's a step-up auth screen, not a page the user can navigate away from.
  const noHeaderFooter =
    pathname === '/dashboard' ||
    pathname === '/intake-form' ||
    pathname === '/mfa' ||
    // The /between-sessions landing carries its own persistent
    // Between Sessions wordmark + Join CTA (mirroring the interactive
    // tour's chrome). The site Header/Footer would collide with those
    // fixed elements and break the brand framing.
    pathname === '/between-sessions' ||
    // /between-sessions/tour is an immersive full-page carousel with its
    // own fixed bottom control bar — the site Header/Footer would sit
    // behind the controls and break the flow. Same treatment for the
    // signup flow that continues from the tour.
    pathname === '/between-sessions/tour' ||
    pathname.startsWith('/between-sessions/tour/');

  // Check if we're on the between sessions join page
  const isBetweenSessionsJoinPage = pathname === '/between-sessions/join';

  // Check if we're on any student-vip page
  const isStudentsPage = pathname.startsWith('/student-vip');

  // Check if we're on the practicum page
  const isPracticumPage = pathname === '/join-us/practicum-program';

  // Check if we're on the between sessions student page
  const isBetweenSessionsStudentPage = pathname === '/join-us/between-sessions-student';

  // BS supervisor browse + individual supervisor profile pages still
  // get the minimalist BS header (no nav, "Join Between Sessions" CTA)
  // for now — those routes don't yet carry the landing/tour's
  // persistent wordmark chrome. The /between-sessions landing itself
  // is handled above by the noHeaderFooter branch because it renders
  // its own fixed wordmark + Join pill.
  const isBetweenSessionsPage =
    pathname === '/between-sessions/supervisors' ||
    pathname.startsWith('/supervisor/');

  // Check if we're on a workshop page
  const isWorkshopPage = pathname.startsWith('/workshops');

  // If we're on an individual workshop page (/workshops/{slug}), extract the slug
  // so the header CTA scrolls to THIS workshop's register form, not the CRPO one.
  const workshopSlugMatch = pathname.match(/^\/workshops\/([^\/]+)/);
  const currentWorkshopSlug = workshopSlugMatch?.[1];
  const workshopCtaHref = currentWorkshopSlug
    ? `/workshops/${currentWorkshopSlug}#register`
    : '/workshops/crpo#register';

  // Check if we're on a tools page (e.g., /tools/crpo-progress-calculator)
  const isToolsPage = pathname.startsWith('/tools');

  // Check if we're on a partner page and extract the partnership ID
  const partnerMatch = pathname.match(/^\/partners\/([^\/]+)/);
  let partnershipId = partnerMatch?.[1];

  // Also check for partnershipId query parameter on ANY page
  const partnershipParam = searchParams.get('partnershipId');
  if (!partnershipId && partnershipParam) {
    partnershipId = partnershipParam;
  }

  // Consider it a partner context if we have a partnership ID from either source
  const isPartnerContext = !!partnershipId;
  
  // Footer configuration
  const footerSections = [
    {
      title: "Support",
      links: [
        { label: "Get Help", href: "/support/help" },
        { label: "Dashboard", href: "/dashboard" },
        { label: "Sign Up", href: "/signup" },
        { label: "Privacy Policy", href: "/privacy" },
        { label: "Terms of Service", href: "/terms" },
        { label: "Trust & Security", href: "/trust" },
        { label: "What's New", href: "/changelog" }
      ]
    },
    {
      title: "For Clients",
      links: [
        { label: "How It Works", href: "/#how-it-works" },
        { label: "Therapists", href: "/therapists" },
        { label: "Pricing", href: "/#pricing" },
        { label: "Podcast", href: "/podcast" },
        { label: "For Students", href: "/student-vip" }
      ]
    },
    {
      title: "For Therapists",
      links: [
        { label: "Between Sessions", href: "/between-sessions" },
        { label: "Free Workshops", href: "/workshops" },
        { label: "CRPO Progress Calculator", href: "/tools/crpo-progress-calculator" },
        { label: "Therapeutic Presence Tool", href: "/tools/therapeutic-presence" }
      ]
    },
    {
      title: "Join Us",
      links: [
        { label: "Practicum Program (Students)", href: "/join-us/practicum-program" },
        { label: "Between Sessions", href: "/between-sessions" }
      ]
    }
  ];
  
  if (noHeaderFooter) {
    return (
      <>
        <main className="min-h-screen">{children}</main>
        {/* Inactivity timeout warning modal */}
        <InactivityWarningModal
          isOpen={showWarning}
          remainingTime={remainingTime}
          onExtend={extendSession}
          onLogout={logout}
        />
      </>
    );
  }
  
  // Default navigation items - show Login if not logged in, Dashboard if logged in
  const defaultNavItems = [
    { label: "How it works", href: "/#how-it-works" },
    { label: "Therapists", href: "/therapists" },
    { label: "Pricing", href: "/#pricing" },
    profile ? { label: "Dashboard", href: "/dashboard" } : { label: "Login", href: "/login" }
  ];
  
  // Custom header props for students page, practicum page, and partner pages
  const headerProps = isPartnerContext ? {
    logoHref: `/partners/${partnershipId}`,
    navItems: [
      { label: "How it works", href: pathname.startsWith('/partners/') ? `#how-it-works` : `/partners/${partnershipId}#how-it-works` },
      { label: "Therapists", href: `/therapists?partnershipId=${partnershipId}` },
      { label: "FAQ", href: pathname.startsWith('/partners/') ? `#faq` : `/partners/${partnershipId}#faq` },
      profile ? { label: "Dashboard", href: "/dashboard" } : { label: "Login", href: "/login" }
    ],
    cta: {
      label: "Get Matched",
      href: `/partners/${partnershipId}/quiz`,
      variant: "primary" as const
    }
  } : isStudentsPage ? {
    navItems: [
      { label: "How it works", href: "/student-vip#how-it-works" },
      { label: "Pricing", href: "/student-vip#pricing" },
      { label: "Therapists", href: "/therapists" },
      profile ? { label: "Dashboard", href: "/dashboard" } : { label: "Login", href: "/login" }
    ],
    cta: {
      label: "Get started",
      href: "/quiz?promo=STUDENTVIP",
      variant: "primary" as const
    }
  } : isPracticumPage ? {
    navItems: [],
    cta: {
      label: "Find Your Path",
      href: "#path-quiz",
      variant: "primary" as const
    }
  } : isBetweenSessionsStudentPage ? {
    navItems: [],
    cta: {
      label: "Join Between Sessions",
      href: "/between-sessions/join?student=true",
      variant: "primary" as const
    }
  } : isBetweenSessionsPage ? {
    navItems: [],
    cta: {
      label: "Join Between Sessions",
      href: "/between-sessions/join",
      variant: "primary" as const
    }
  } : isBetweenSessionsJoinPage ? {
    navItems: [],
    cta: undefined
  } : isWorkshopPage ? {
    navItems: [],
    cta: {
      label: "Save Your Spot",
      href: workshopCtaHref,
      variant: "primary" as const
    }
  } : isToolsPage ? {
    navItems: [],
    cta: {
      label: "Learn About Between Sessions",
      href: "/between-sessions",
      variant: "secondary" as const
    }
  } : {
    navItems: defaultNavItems
  };
  
  return (
    <>
      <Header {...headerProps} />
      <main className="min-h-screen pt-20">
        {children}
      </main>
      <Footer
        definition={{
          term: "Al·li",
          meaning: "A person or entity who supports and empowers others."
        }}
        sections={footerSections}
      />
      {/* Inactivity timeout warning modal */}
      <InactivityWarningModal
        isOpen={showWarning}
        remainingTime={remainingTime}
        onExtend={extendSession}
        onLogout={logout}
      />
    </>
  );
}

export function ConditionalLayout({ children }: ConditionalLayoutProps) {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    }>
      <ConditionalLayoutContent>{children}</ConditionalLayoutContent>
    </Suspense>
  );
}